import { Typography, Stack, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, useTheme, } from "@mui/material";
import React, { memo, useCallback, useEffect, useState } from "react";
import { useSwipeable } from "react-swipeable";
const SIZE = 4;
const STORAGE_KEY = "game2048-state-v2"; // 本版存储 key
const MAX_HISTORY_LENGTH = 10; // 最大历史记录长度
const emptyCells = (board) => {
    const cells = [];
    for (let r = 0; r < SIZE; r++) {
        for (let c = 0; c < SIZE; c++) {
            if (board[r][c].value === 0) {
                cells.push({ r, c });
            }
        }
    }
    return cells;
};
const addRandomNumber = (board) => {
    const cells = emptyCells(board);
    if (cells.length === 0)
        return board;
    const randomIndex = Math.floor(Math.random() * cells.length);
    const { r, c } = cells[randomIndex];
    const newValue = Math.random() < 0.9 ? 2 : 4;
    board[r][c] = { value: newValue, status: "normal" };
    return board;
};
const createNewBoard = () => {
    const newBoard = Array.from({ length: SIZE }, () => Array(SIZE).fill({ value: 0, status: "normal" }));
    addRandomNumber(newBoard);
    addRandomNumber(newBoard);
    return newBoard;
};
const clockwiseRotateBoard = (board) => {
    const newBoard = Array.from({ length: SIZE }, () => Array(SIZE).fill(0));
    for (let r = 0; r < SIZE; r++) {
        for (let c = 0; c < SIZE; c++) {
            newBoard[c][SIZE - 1 - r] = board[r][c];
        }
    }
    return newBoard;
};
const anticlockwiseRotateBoard = (board) => {
    const newBoard = Array.from({ length: SIZE }, () => Array(SIZE).fill(0));
    for (let r = 0; r < SIZE; r++) {
        for (let c = 0; c < SIZE; c++) {
            newBoard[SIZE - 1 - c][r] = board[r][c];
        }
    }
    return newBoard;
};
const Cell = memo(({ v, r, c }) => {
    const theme = useTheme();
    console.log("Rendering Cell:", r, c, v);
    const getColor = (value) => {
        console.log("getColor called with value:", value);
        switch (value) {
            case 0:
                return "#ccc0b3";
            case 2:
                return "#eee4da";
            case 4:
                return "#ede0c8";
            case 8:
                return "#f2b179";
            case 16:
                return "#f59563";
            case 32:
                return "#f67c5f";
            case 64:
                return "#f65e3b";
            case 128:
                return "#edcf72";
            case 256:
                return "#edcc61";
            case 512:
                return "#edc850";
            case 1024:
                return "#edc53f";
            case 2048:
                return "#edc22e";
            default:
                return theme.palette.primary.main;
        }
    };
    return (React.createElement(Box, { sx: {
            bgcolor: getColor(v.value),
            color: v.value > 0 ? "#fff" : "transparent",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: v.value >= 1000
                ? theme.typography.h4
                : v.value >= 100
                    ? theme.typography.h5
                    : theme.typography.h6,
            fontWeight: 700,
            borderRadius: 1,
            width: theme.spacing(8),
            height: theme.spacing(8),
        } }, v.value > 0 && v.value));
});
Cell.displayName = "Cell";
export const GamePage = memo(() => {
    const [gameStatus, setGameStatus] = useState(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                return JSON.parse(saved);
            }
            catch (_a) {
                // ignore parse error and fall back to new game
            }
        }
        return {
            board: createNewBoard(),
            score: 0,
        };
    });
    const [history, setHistory] = useState([]);
    const [over, setOver] = useState(false);
    const [snack, setSnack] = useState(false);
    const mergeLeft = useCallback((status, onChange) => {
        const board = status.board;
        const newBoard = [];
        let stepScore = 0;
        for (let r = 0; r < SIZE; r++) {
            const row = board[r]
                .filter((v) => v.value > 0)
                .map((v) => ({ value: v.value, status: "normal" }));
            let index = 0;
            while (index < row.length) {
                if (index < row.length - 1 &&
                    row[index].value === row[index + 1].value) {
                    // 合并相同的数字
                    row[index] = {
                        value: row[index].value * 2,
                        status: "merged",
                    };
                    stepScore += row[index].value;
                    row.splice(index + 1, 1); // 删除下一个元素
                }
                index++;
            }
            // 填充空位
            while (row.length < SIZE) {
                row.push({ value: 0, status: "normal" });
            }
            newBoard.push(row);
        }
        // 检查是否有变化
        const hasChanged = newBoard.some((newRow, r) => newRow.some((v, c) => v.value !== board[r][c].value));
        // 如果没有变化，直接返回原状态
        if (!hasChanged) {
            return status;
        }
        // 添加新的随机数
        addRandomNumber(newBoard);
        // 检查是否游戏结束
        let isGameOver = newBoard.flat().every((v) => v.value > 0);
        if (!isGameOver) {
            // 检查是否还有可合并的数字
            for (let r = 0; r < SIZE; r++) {
                for (let c = 0; c < SIZE - 1; c++) {
                    if (newBoard[r][c] === newBoard[r][c + 1]) {
                        isGameOver = false; // 还有可合并的数字，游戏未结束
                    }
                }
            }
            for (let c = 0; c < SIZE; c++) {
                for (let r = 0; r < SIZE - 1; r++) {
                    if (newBoard[r][c] === newBoard[r + 1][c]) {
                        isGameOver = false; // 还有可合并的数字，游戏未结束
                    }
                }
            }
        }
        if (isGameOver) {
            setOver(true);
        }
        // 检查是否达成 2048
        if (newBoard.some((row) => row.some((cell) => cell.value === 2048))) {
            setSnack(true);
        }
        onChange();
        return {
            board: newBoard,
            score: status.score + stepScore,
        };
    }, []);
    const addHistory = useCallback((status) => {
        console.log("Adding history:", status);
        if (history.length >= MAX_HISTORY_LENGTH) {
            setHistory((prevHistory) => [...prevHistory.slice(1), status]);
        }
        else {
            setHistory((prevHistory) => [...prevHistory, status]);
        }
    }, [gameStatus, history.length]);
    const handleSwipe = useCallback((dir) => {
        console.log(gameStatus, dir);
        switch (dir) {
            case "Left":
                setGameStatus((prevStatus) => mergeLeft(prevStatus, () => {
                    addHistory(prevStatus);
                }));
                break;
            case "Right":
                setGameStatus((prevStatus) => {
                    const updatedStatus = mergeLeft({
                        board: prevStatus.board.map((row) => row.reverse()),
                        score: prevStatus.score,
                    }, () => {
                        addHistory(prevStatus);
                    });
                    return {
                        board: updatedStatus.board.map((row) => row.reverse()),
                        score: updatedStatus.score,
                    };
                });
                break;
            case "Up":
                setGameStatus((prevStatus) => {
                    const rotatedBoard = anticlockwiseRotateBoard(prevStatus.board);
                    const updatedStatus = mergeLeft({
                        board: rotatedBoard,
                        score: prevStatus.score,
                    }, () => {
                        addHistory(prevStatus);
                    });
                    return {
                        board: clockwiseRotateBoard(updatedStatus.board),
                        score: updatedStatus.score,
                    };
                });
                break;
            case "Down":
                setGameStatus((prevStatus) => {
                    const rotatedBoard = clockwiseRotateBoard(prevStatus.board);
                    const updatedStatus = mergeLeft({
                        board: rotatedBoard,
                        score: prevStatus.score,
                    }, () => {
                        addHistory(prevStatus);
                    });
                    return {
                        board: anticlockwiseRotateBoard(updatedStatus.board),
                        score: updatedStatus.score,
                    };
                });
                break;
            default:
                break;
        }
    }, []);
    const swipeHandler = useSwipeable({
        onSwiped: (eventData) => {
            const { dir } = eventData;
            handleSwipe(dir);
        },
        trackMouse: true,
        trackTouch: true,
    });
    const reset = useCallback(() => {
        setGameStatus({
            board: createNewBoard(),
            score: 0,
        });
        setHistory([]);
    }, []);
    const undo = useCallback(() => {
        if (history.length === 0)
            return;
        const lastState = history[history.length - 1];
        setGameStatus(lastState);
        setHistory((prevHistory) => prevHistory.slice(0, -1));
    }, [history]);
    useEffect(() => {
        // save game status to localStorage
        localStorage.setItem(STORAGE_KEY, JSON.stringify(gameStatus));
    }, [gameStatus]);
    return (React.createElement(Stack, { direction: "column", alignItems: "center", width: "100%", spacing: 2 },
        React.createElement(Typography, { variant: "h2", sx: { mt: 3, mb: 1, color: "#776e65", fontWeight: 700 } }, "2048"),
        React.createElement(Stack, { direction: "row", spacing: 2, justifyContent: "center", alignItems: "center", mb: 3 },
            React.createElement(Box, { sx: {
                    bgcolor: "#bbada0",
                    color: "#fff",
                    px: 3,
                    py: 1,
                    borderRadius: 1,
                    textAlign: "center",
                } },
                React.createElement(Typography, { variant: "subtitle1" }, "\u5206\u6570"),
                React.createElement(Typography, { variant: "h5" }, gameStatus.score)),
            React.createElement(Button, { variant: "contained", onClick: reset }, "\u91CD\u65B0\u5F00\u59CB"),
            React.createElement(Button, { variant: "outlined", onClick: undo, disabled: !history.length }, "\u64A4\u9500")),
        React.createElement(Box, { sx: {
                mx: "auto",
                mb: 4,
                display: "grid",
                gridTemplateColumns: `repeat(${SIZE},1fr)`,
                gap: 1,
                p: 1,
                bgcolor: "#bbada0",
                borderRadius: 1,
            }, ...swipeHandler }, gameStatus.board.map((row, r) => row.map((v, c) => (React.createElement(Cell, { key: r + "-" + c + "-" + v.value, v: v, r: r, c: c }))))),
        React.createElement(Dialog, { open: over },
            React.createElement(DialogTitle, null, "\u6E38\u620F\u7ED3\u675F"),
            React.createElement(DialogContent, null,
                React.createElement(Typography, null,
                    "\u6700\u7EC8\u5F97\u5206\uFF1A",
                    gameStatus.score)),
            React.createElement(DialogActions, null,
                React.createElement(Button, { onClick: reset }, "\u518D\u6765\u4E00\u5C40"))),
        React.createElement(Snackbar, { open: snack, autoHideDuration: 3000, onClose: () => setSnack(false), anchorOrigin: { vertical: "top", horizontal: "center" } },
            React.createElement(Alert, { severity: "success", sx: { fontSize: 18 } }, "\u8FBE\u5230 2048 \uFF01\u7EE7\u7EED\u51B2\uFF1F"))));
});
GamePage.displayName = "GamePage";
